import { cliffOf, GRASS, paintedAs, SUMMIT, SUMMIT_CLIFF, SUMMIT_LEVEL, terrainOf, tilesetFor, tilesetOf, } from '../data/terrains.js';
import { cliffTileOf, cliffWindow, cornerLevels, isCliffCell, PLAIN_UPPER_WINDOW, } from './relief.js';
export class TerrainField {
    width;
    height;
    chars;
    levels;
    /** `heights`: one digit per tile; without it the zone is flat. */
    constructor(ground, heights = []) {
        this.height = ground.length;
        this.width = ground[0]?.length ?? 0;
        this.chars = ground.map((row) => row);
        this.levels = heights;
    }
    /** The height of a tile, clamped to the map like the terrain. */
    levelAt = (x, y) => {
        const cx = Math.min(this.width - 1, Math.max(0, x));
        const cy = Math.min(this.height - 1, Math.max(0, y));
        return Number(this.levels[cy]?.[cx] ?? 0);
    };
    /** The four corner heights of tile (x, y): NW, NE, SW, SE. */
    levelCornersOf(x, y) {
        return cornerLevels(this.levelAt, x, y);
    }
    /**
     * Whether the tile is drawn as part of a cliff: a cliff or the stairs up it.
     * Only a ground that can rise has one; rock the rim swallowed stays rock.
     */
    isCliffCell(x, y) {
        const ground = this.paintedAt(x, y);
        // The rock round a summit (ADR 0021): only the summit's height counts there.
        if (ground === ROCK)
            return isCliffCell(this.summitAt, x, y);
        return cliffOf(ground) !== undefined && isCliffCell(this.levelAt, x, y);
    }
    /** Which ends of a shadow are open, where the neighbours cast none: it tapers there. */
    shadowEnds(x, y) {
        return { west: !this.castsShadowAt(x - 1, y), east: !this.castsShadowAt(x + 1, y) };
    }
    /**
     * Whether a south face casts its shadow on this tile: the first row of
     * open ground below a face, the plateau three rows up, along a face at
     * least two tiles wide (a lone column of shade reads as a smudge, not a
     * shadow). The shadow is stepped over `SHADOW_ROWS` rows (`shadowAlpha`),
     * drawn over the ground; never on water, which the relief never borders.
     */
    castsShadowAt(x, y) {
        if (!this.underFace(x, y))
            return false;
        return this.underFace(x - 1, y) || this.underFace(x + 1, y);
    }
    underFace(x, y) {
        if (this.isCliffCell(x, y) || !terrainOf(this.charAt(x, y)).walkable)
            return false;
        return this.standingAt(x, y - 3) > this.standingAt(x, y) && this.isCliffCell(x, y - 1);
    }
    /** The height a tile is seen at: its terrace, a rock mass halfway up, a summit on top. */
    standingAt(x, y) {
        const char = this.charAt(x, y);
        if (char === SUMMIT)
            return SUMMIT_LEVEL;
        if (char === ROCK)
            return 1;
        return Math.min(...this.levelCornersOf(x, y));
    }
    /** Heights as the summit's cliff sees them: the summit high, everything else low. */
    summitAt = (x, y) => this.levelAt(x, y) >= SUMMIT_LEVEL ? 1 : 0;
    /** The terrain character of a tile; outside the map reads as the nearest tile. */
    charAt(x, y) {
        const cx = Math.min(this.width - 1, Math.max(0, x));
        const cy = Math.min(this.height - 1, Math.max(0, y));
        return terrainOf(this.chars[cy]?.[cx] ?? GRASS).char;
    }
    /** What a tile is painted as: a bridge shows the river under it. */
    paintedAt(x, y) {
        return paintedAs(this.charAt(x, y));
    }
    /** Terrain of the vertex at the top-left corner of tile (vx, vy). */
    vertexAt(vx, vy) {
        let found = this.paintedAt(vx, vy);
        let layer = terrainOf(found).layer;
        for (const [dx, dy] of CORNER_TILES) {
            const char = this.paintedAt(vx + dx, vy + dy);
            const candidate = terrainOf(char).layer;
            // Ties go to the lower character code, so the choice never depends on scan order.
            if (candidate > layer || (candidate === layer && char < found)) {
                found = char;
                layer = candidate;
            }
        }
        return found;
    }
    /** The four corner terrains of tile (x, y): NW, NE, SW, SE. */
    cornersOf(x, y) {
        return [
            this.vertexAt(x, y),
            this.vertexAt(x + 1, y),
            this.vertexAt(x, y + 1),
            this.vertexAt(x + 1, y + 1),
        ];
    }
    /**
     * The tile to draw at (x, y), or null when no tileset holds its corners.
     * At the few junctions of three grounds the generator cannot untie (a
     * bank, the rim), one ground's corners, grass first, are drawn as another
     * ground there: a knot shows a sliver of the wrong bank rather than a
     * square of meadow.
     */
    tileAt(x, y) {
        if (this.isCliffCell(x, y))
            return this.cliffAt(x, y);
        // A summit is painted whole: the plain upper tile of its cliff.
        if (this.charAt(x, y) === SUMMIT)
            return { tileset: SUMMIT_CLIFF, mask: PLAIN_UPPER };
        const corners = this.cornersOf(x, y);
        const direct = pairTile(corners);
        if (direct !== null)
            return direct;
        const grounds = [...new Set(corners)]
            .filter((c) => terrainOf(c).walkable)
            .sort((a, b) => (a === GRASS ? -1 : b === GRASS ? 1 : 0));
        for (const minor of grounds) {
            for (const other of new Set(corners)) {
                if (other === minor || !terrainOf(other).walkable)
                    continue;
                const patched = pairTile(corners.map((c) => (c === minor ? other : c)));
                if (patched !== null)
                    return patched;
            }
        }
        return null;
    }
    /** Whether the tile's own corners are drawable, without the knot patch: what the generator must reach. */
    isKnot(x, y) {
        return !this.isCliffCell(x, y) && pairTile(this.cornersOf(x, y)) === null;
    }
    /**
     * The vertices around a cliff cell as its tile sees them (world/relief.ts),
     * null when the cell is no cliff, spans three heights or its ground cannot
     * rise. The rock round a summit sees only the summit's height.
     */
    cliffWindowAt(x, y) {
        if (!this.isCliffCell(x, y))
            return null;
        if (this.paintedAt(x, y) === ROCK)
            return cliffWindow(this.summitAt, x, y);
        const levels = this.levelCornersOf(x, y);
        if (Math.max(...levels) - Math.min(...levels) > 1)
            return null;
        if (cliffOf(this.paintedAt(x, y)) === undefined)
            return null;
        return cliffWindow(this.levelAt, x, y);
    }
    /** A cliff cell, from its ground's cliff tileset (or the summit's). */
    cliffAt(x, y) {
        const window = this.cliffWindowAt(x, y);
        if (window === null)
            return null;
        const tileset = this.paintedAt(x, y) === ROCK ? SUMMIT_CLIFF : cliffOf(this.paintedAt(x, y))?.key;
        if (tileset === undefined)
            return null;
        return { tileset, mask: cliffTileOf(window) };
    }
    /** Every tile no tileset can draw. */
    conflicts() {
        const found = [];
        for (let y = 0; y < this.height; y += 1) {
            for (let x = 0; x < this.width; x += 1) {
                if (this.tileAt(x, y) === null)
                    found.push({ x, y });
            }
        }
        return found;
    }
}
const ROCK = 'r';
/** Rows of pixels a cast shadow covers below its face: most of one tile. */
export const SHADOW_ROWS = 12;
/** Its darkness in three hard steps, pixel art rather than a blur; the first is the foot's (build:cliffs). */
export const SHADOW_STEPS = [0.3, 0.2, 0.1];
/** The cast shadow's darkness at a pixel of its tile: dense under the wall, gone at the end. */
export function shadowAlpha(row, column = 8, open = { west: false, east: false }) {
    let alpha = SHADOW_STEPS[Math.floor(row / 4)] ?? 0;
    // Where the face ends, the shade thins out over four pixels instead of stopping square.
    const taper = (distance) => Math.min(1, Math.floor(distance / 2) / 2);
    if (open.west)
        alpha *= taper(column);
    if (open.east)
        alpha *= taper(15 - column);
    return alpha;
}
/** The cliff tile whose sixteen vertices are all upper: a summit's plain stone. */
const PLAIN_UPPER = cliffTileOf(PLAIN_UPPER_WINDOW.split('').map(Number));
/** The tile of the one tileset holding these corners, if any. */
function pairTile(corners) {
    const kinds = [...new Set(corners)];
    if (kinds.length > 2)
        return null;
    const [a = GRASS, b = a] = kinds;
    const tileset = a === b ? tilesetOf(a) : tilesetFor(a, b);
    if (tileset === undefined)
        return null;
    let mask = 0;
    corners.forEach((corner, index) => {
        if (corner === tileset.upper)
            mask |= 8 >> index;
    });
    return { tileset: tileset.key, mask };
}
/** Offsets of the four tiles around the vertex at a tile's top-left corner. */
const CORNER_TILES = [
    [-1, -1],
    [0, -1],
    [-1, 0],
    [0, 0],
];
//# sourceMappingURL=terrain-field.js.map